
import React, { useEffect } from 'react';
import { useFirstBusinessForm } from './hooks/useFirstBusinessForm';
import SearchSection from './SearchSection';
import FormContent from './FormContent';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useBusinessFormContext } from '@/context/BusinessFormContext';
// import { useSectionNavigation } from './hooks/useSectionNavigation'; // No longer used
import { useNavigate } from 'react-router-dom';

interface FirstBusinessFormProps {
  onComplete: (businessId: string) => void;
  onGoBack?: () => void;
}

// Type for the BackButton component
interface BackButtonProps {
  onGoHome?: () => void;
}

// Define the component type with static property
interface FirstBusinessFormComponent extends React.FC<FirstBusinessFormProps> {
  BackButton: React.FC<BackButtonProps>;
}

const FirstBusinessForm: React.FC<FirstBusinessFormProps> = ({
  onComplete,
  onGoBack
}) => {
  const navigate = useNavigate();
  const { formSection, setFormSection: setContextFormSection } = useBusinessFormContext(); // Use context's setter for navigation
  
  // useFirstBusinessForm will be refactored to use BusinessFormContext.
  // For now, it still returns many values, but we'll only use what's necessary.
  const { 
    handleBusinessSelect, // Still needed for SearchSection
    handleSubmit,         // Needed for FormContent's main submit button
    handleCreateNew,      // Needed for SearchSection
    isSubmitting,         // Destructure isSubmitting
    formError,            // Destructure formError
    // Most other returned values from useFirstBusinessForm are now in BusinessFormContext
    // and will be accessed by child components directly.
  } = useFirstBusinessForm(onComplete);

  console.log("FirstBusinessForm: Current form section from context:", formSection);
  
  useEffect(() => {
    console.log("FirstBusinessForm: formSection (from context) changed to:", formSection);
  }, [formSection]);
  
  if (formSection === 'search') {
    return (
      <SearchSection 
        onBusinessSelect={(business) => {
          handleBusinessSelect(business); // Call original handler from hook
        }}
        onCreateNew={handleCreateNew} // Pass handleCreateNew from useFirstBusinessForm
        onBackButtonClick={onGoBack} // This is for navigating away from the entire FirstBusinessForm flow
      />
    );
  }
  
  // When formSection is 'form'
  return (
    <FormContent
      handleSubmit={handleSubmit} // Pass the main submit handler from the hook
      isSubmitting={isSubmitting} // Pass isSubmitting
      formError={formError}       // Pass formError
      // onBackButtonClick is not directly used by FormContent, but could be if FormContent had its own back button.
      // The FirstBusinessForm.BackButton handles navigation between 'form' and 'search' sections.
    />
  );
};

// Back button component that uses the current form section to determine behavior
// This BackButton is used externally, e.g. in the page header.
const BackButton: React.FC<BackButtonProps> = ({ onGoHome }) => { // onGoHome is for when in 'search' and going further back
  const { formSection, setFormSection } = useBusinessFormContext(); // Consumes context
  const navigate = useNavigate(); // To navigate away if onGoHome is not provided

  const handleClick = () => {
    if (formSection === 'form') {
      // If on the form, go back to the search section
      setFormSection('search');
    } else if (formSection === 'search') {
      // If on search section, either call onGoHome or navigate to a default "back" location
      if (onGoHome) {
        onGoHome();
      } else {
        navigate(-1); // Or a specific path like '/'
      }
    }
  };
  
  return (
    <Button 
      variant="ghost" 
      size="sm" 
      onClick={handleClick} 
      className="flex items-center gap-1 text-gray-600 hover:text-black"
    >
      <ArrowLeft size={16} />
      Back
    </Button>
  );
};

// Attach the BackButton as a static property
(FirstBusinessForm as FirstBusinessFormComponent).BackButton = BackButton;

export default FirstBusinessForm as FirstBusinessFormComponent;
